import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Sparkles, GitFork, Trash2, Download, FileCode2, Clock, Users, Layout, Gift } from "lucide-react";
import { BetaFeedbackButton } from "@/components/BetaFeedback";
import { CompanionWidget } from "@/components/CompanionWidget";
import VerifyLintCard from "@/components/VerifyLintCard";
import { WatchlistWidget } from "@/components/WatchlistWidget";
import { PortfolioCard } from "@/components/PortfolioCard";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { MorningBriefCard } from "@/components/MorningBriefCard";
import { WeeklyRetroCard } from "@/components/WeeklyRetroCard";

export default function Dashboard() {
  const [strategies, setStrategies] = useState([]);
  const [shared, setShared] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [own, sh, tpl] = await Promise.all([
        api.get("/strategies"),
        api.get("/strategies/shared-with-me").catch(()=>({data:[]})),
        api.get("/templates").catch(()=>({data:[]})),
      ]);
      setStrategies(own.data);
      setShared(sh.data || []);
      setTemplates(tpl.data || []);
    } catch {
      toast.error("Failed to load strategies");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyTemplate = async (t) => {
    try {
      const { data } = await api.post(`/templates/${t.template_id}/use`);
      toast.success(`Cloned template → ${data.name}`);
      nav(`/builder/${data.strategy_id}`);
    } catch { toast.error("Could not clone template"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this strategy? This cannot be undone.")) return;
    try {
      await api.delete(`/strategies/${id}`);
      toast.success("Strategy deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const download = async (s) => {
    // fetch code to blob with auth header
    try {
      const token = getToken();
      const res = await fetch(`${API}/strategies/${s.strategy_id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const text = await res.text();
      const blob = new Blob([text], { type: "text/x-mql4" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (s.name || "Strategy").replace(/[^A-Za-z0-9_]+/g, "_");
      a.href = url; a.download = `${safe}.mq4`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="chip mb-3" data-testid="dashboard-eyebrow">STRATEGY VAULT</div>
            <h1 className="font-display text-4xl text-white tracking-tight">Your Strategies</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Every MQL4 robot you've forged, in one place.</p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Link data-testid="dash-new-visual-btn" to="/builder" className="btn-primary flex items-center gap-2">
              <Plus size={14}/> New strategy
            </Link>
            <Link data-testid="dash-new-ai-btn" to="/ai" className="btn-outline flex items-center gap-2">
              <Sparkles size={14}/> AI generate
            </Link>
            <Link data-testid="dash-new-clone-btn" to="/clone" className="btn-outline flex items-center gap-2">
              <GitFork size={14}/> Clone existing
            </Link>
            <Link data-testid="dash-gallery-btn" to="/gallery" className="btn-outline flex items-center gap-2">
              <Users size={14}/> Gallery
            </Link>
            <Link data-testid="dash-referrals-btn" to="/referrals" className="btn-outline flex items-center gap-2">
              <Gift size={14}/> Refer
            </Link>
          </div>
        </div>

        <CompanionWidget/>

        <MorningBriefCard/>

        <WeeklyRetroCard/>

        <OnboardingChecklist/>

        <PortfolioCard/>

        <WatchlistWidget/>

        {templates.length > 0 && (
          <section className="mb-10" data-testid="dashboard-templates">
            <div className="flex items-center gap-2 mb-3">
              <Layout size={14} className="text-[var(--accent)]"/>
              <h2 className="font-display text-lg text-white">Start from a template</h2>
              <span className="chip">6 curated</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.map((t) => (
                <button key={t.template_id}
                  onClick={()=>applyTemplate(t)}
                  data-testid={`template-${t.template_id}`}
                  className="card-sharp p-4 text-left hover:!border-[var(--accent)]/60 transition-colors group">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`chip ${t.difficulty === "advanced" ? "!border-[var(--loss)]/40 !text-[var(--loss)]"
                      : t.difficulty === "intermediate" ? "!border-yellow-400/40 !text-yellow-400"
                      : "!border-[var(--profit)]/40 !text-[var(--profit)]"}`}>{t.difficulty}</span>
                    <span className="chip">{t.symbol}</span>
                    <span className="chip">{(t.timeframe || "").replace("PERIOD_","")}</span>
                  </div>
                  <h3 className="font-display text-base text-white group-hover:text-[var(--accent)] transition-colors">{t.name}</h3>
                  <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2 font-mono">{t.summary}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Loading…</div>
        ) : strategies.length === 0 ? (
          <div className="card-sharp p-12 text-center" data-testid="dash-empty-state">
            <FileCode2 className="mx-auto text-[var(--text-muted)]" size={40}/>
            <h3 className="font-display text-2xl text-white mt-4">No strategies yet</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
              Start with the visual builder, describe an idea to the AI, or clone an existing .mq4.
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Link to="/builder" className="btn-primary flex items-center gap-2"><Plus size={14}/> Start with builder</Link>
              <Link to="/ai" className="btn-outline flex items-center gap-2"><Sparkles size={14}/> Ask AI</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dash-strategy-grid">
            {strategies.map((s) => (
              <div key={s.strategy_id} className="card-sharp p-5 flex flex-col" data-testid={`strategy-card-${s.strategy_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{s.source}</div>
                    <h3 className="font-display text-xl text-white mt-1">{s.name}</h3>
                  </div>
                  <FileCode2 className="text-[var(--accent)]" size={16}/>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <span className="chip">{s.config?.symbol || "—"}</span>
                  <span className="chip">{(s.config?.timeframe || "PERIOD_H1").replace("PERIOD_","")}</span>
                  {(s.config?.entry_rules?.length ?? 0) > 0 && (
                    <span className="chip chip-profit">{s.config.entry_rules.length} entry</span>
                  )}
                  {(s.config?.exit_rules?.length ?? 0) > 0 && (
                    <span className="chip chip-loss">{s.config.exit_rules.length} exit</span>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-1.5 text-[10px] font-mono text-[var(--text-muted)]">
                  <Clock size={11}/> {new Date(s.updated_at).toLocaleString()}
                </div>
                <div className="mt-5 pt-4 border-t border-white/5 flex gap-2">
                  <button data-testid={`edit-${s.strategy_id}`} onClick={()=>nav(`/builder/${s.strategy_id}`)}
                    className="btn-outline flex-1 text-xs py-2">Open</button>
                  <button data-testid={`download-${s.strategy_id}`} onClick={()=>download(s)}
                    className="p-2 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"><Download size={14}/></button>
                  <button data-testid={`delete-${s.strategy_id}`} onClick={()=>del(s.strategy_id)}
                    className="p-2 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)] transition-colors"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {shared.length > 0 && (
          <section className="mt-14" data-testid="shared-with-me-section">
            <div className="flex items-center gap-2 mb-4">
              <Users size={16} className="text-[var(--accent)]"/>
              <h2 className="font-display text-2xl text-white">Shared with you</h2>
              <span className="chip">{shared.length}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shared.map((s) => (
                <div key={s.share_id} className="card-sharp p-5 flex flex-col" data-testid={`shared-card-${s.strategy_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">shared · {s.permission}</div>
                      <h3 className="font-display text-xl text-white mt-1">{s.name}</h3>
                      <div className="text-[11px] font-mono text-[var(--text-secondary)] mt-1">by {s.owner_email}</div>
                    </div>
                    <FileCode2 className="text-[var(--accent)]" size={16}/>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className="chip">{s.config?.symbol || "—"}</span>
                    <span className="chip">{(s.config?.timeframe || "PERIOD_H1").replace("PERIOD_","")}</span>
                  </div>
                  <div className="mt-5 pt-4 border-t border-white/5 flex gap-2">
                    <button data-testid={`shared-open-${s.strategy_id}`} onClick={()=>nav(`/builder/${s.strategy_id}`)}
                      className="btn-outline flex-1 text-xs py-2">Open</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10" data-testid="dashboard-devtools">
          <div className="flex items-center gap-2 mb-3">
            <span className="chip">DEV TOOLS</span>
            <span className="text-[10px] font-mono text-[var(--text-muted)]">Ground truth linter</span>
          </div>
          <VerifyLintCard/>
        </section>
      </main>
      <BetaFeedbackButton/>
    </div>
  );
}
